/**
 * The console's ready-builds library: finished, signed APKs that anyone with
 * console access can download as-is. The opposite of the Agent APK page —
 * there the APK is stamped per customer; here the bytes uploaded are the
 * bytes downloaded.
 *
 * A build lands by drag & drop (or the file picker behind the same zone), can
 * carry notes, and starts out visible to the whole console. One build may be
 * pinned to a single login while it is not ready for the team; the server
 * simply stops listing it for anyone else.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type DragEvent } from "react";

import { api } from "../api/client";
import type { ReadyBuild } from "../api/types";
import { Empty, ErrorBox, Loading, formatBytes, relativeTime } from "../components/common";
import { useI18n } from "../i18n";

export default function ReadyBuilds() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const builds = useQuery({ queryKey: ["ready-builds"], queryFn: api.readyBuilds });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["ready-builds"] });

  // --- the drop zone -------------------------------------------------------
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);

  const accept = (candidate: File | undefined) => {
    setUploadError(null);
    if (candidate && !candidate.name.toLowerCase().endsWith(".apk")) {
      setUploadError(t("readyBuilds.notApk", { name: candidate.name }));
      return;
    }
    setFile(candidate ?? null);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    accept(event.dataTransfer.files?.[0]);
  };

  const upload = useMutation({
    mutationFn: () => api.uploadReadyBuild(file!, notes.trim() || undefined),
    onSuccess: () => {
      setFile(null);
      setNotes("");
      setUploadError(null);
      refresh();
    },
    onError: (error) => setUploadError(error instanceof Error ? error.message : String(error)),
  });

  // --- the table -----------------------------------------------------------
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [confirming, setConfirming] = useState<string | null>(null);

  const download = useMutation({
    mutationFn: (id: string) => api.readyBuildDownloadUrl(id),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });
  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string; notes?: string; visible_to_everyone?: boolean }) =>
      api.updateReadyBuild(id, body),
    onSuccess: () => {
      setEditingNotes(null);
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteReadyBuild(id),
    onSuccess: () => {
      setConfirming(null);
      refresh();
    },
  });

  // --- the share link ------------------------------------------------------
  // The token is the id a customer reads off the page; the URL behind it is
  // what actually gets passed around, so the button copies the whole URL.
  const [copied, setCopied] = useState<string | null>(null);

  const copyLink = async (build: ReadyBuild) => {
    try {
      await navigator.clipboard.writeText(build.share_url);
    } catch {
      // A refused clipboard (insecure origin, missing permission) leaves the
      // URL in the title attribute, where it can still be selected by hand.
      return;
    }
    setCopied(build.id);
    window.setTimeout(
      () => setCopied((current) => (current === build.id ? null : current)),
      2000,
    );
  };

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <h2>{t("readyBuilds.title")}</h2>
          <p className="page-sub">{t("readyBuilds.intro")}</p>
        </div>
      </header>

      {builds.isLoading && <Loading what={t("readyBuilds.title")} />}
      {builds.error && <ErrorBox error={builds.error} />}
      {upload.error && <ErrorBox error={upload.error} />}
      {update.error && <ErrorBox error={update.error} />}
      {remove.error && <ErrorBox error={remove.error} />}
      {download.error && <ErrorBox error={download.error} />}

      <section
        className={`card dropzone${dragging ? " dragging" : ""}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        {file ? (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              upload.mutate();
            }}
          >
            <p>
              <strong className="mono">{file.name}</strong>{" "}
              <span className="muted small">({formatBytes(file.size)})</span>
            </p>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder={t("readyBuilds.notesPlaceholder")}
              rows={2}
            />
            <p className="muted small">{t("readyBuilds.visibleHint")}</p>
            <div className="row-actions">
              <button className="primary" type="submit" disabled={upload.isPending}>
                {t("readyBuilds.upload")}
              </button>
              <button type="button" onClick={() => setFile(null)}>
                {t("readyBuilds.cancel")}
              </button>
            </div>
          </form>
        ) : (
          <Empty>
            {t("readyBuilds.dropHere")}{" "}
            <button type="button" className="link" onClick={() => fileInput.current?.click()}>
              {t("readyBuilds.pickFile")}
            </button>
          </Empty>
        )}
        <input
          ref={fileInput}
          type="file"
          accept=".apk"
          hidden
          onChange={(event) => accept(event.target.files?.[0] ?? undefined)}
        />
      </section>
      {uploadError && <ErrorBox error={new Error(uploadError)} />}

      {builds.data?.length === 0 && (
        <section className="card">
          <Empty>{t("readyBuilds.empty")}</Empty>
        </section>
      )}

      {!!builds.data?.length && (
        <section className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("readyBuilds.colBuild")}</th>
                  <th>{t("readyBuilds.colNotes")}</th>
                  <th>{t("readyBuilds.colVisible")}</th>
                  <th>{t("readyBuilds.colAdded")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {builds.data.map((build: ReadyBuild) => (
                  <tr key={build.id}>
                    <td>
                      <strong className="mono">{build.filename}</strong>
                      <div className="muted small">
                        {formatBytes(build.size)} · {build.sha256.slice(0, 12)}
                      </div>
                      <div className="muted small mono nowrap" title={build.share_url}>
                        {build.share_token.slice(0, 10)}…
                      </div>
                    </td>
                    <td>
                      {editingNotes === build.id ? (
                        <form
                          className="stack"
                          onSubmit={(event) => {
                            event.preventDefault();
                            update.mutate({ id: build.id, notes: notesDraft });
                          }}
                        >
                          <textarea
                            value={notesDraft}
                            onChange={(event) => setNotesDraft(event.target.value)}
                            rows={2}
                            autoFocus
                          />
                          <div className="row-actions">
                            <button className="primary" type="submit">
                              {t("readyBuilds.save")}
                            </button>
                            <button type="button" onClick={() => setEditingNotes(null)}>
                              {t("readyBuilds.cancel")}
                            </button>
                          </div>
                        </form>
                      ) : (
                        <>
                          {build.notes ?? <span className="muted">—</span>}{" "}
                          <button
                            type="button"
                            className="link"
                            onClick={() => {
                              setEditingNotes(build.id);
                              setNotesDraft(build.notes ?? "");
                            }}
                          >
                            {t("readyBuilds.editNotes")}
                          </button>
                        </>
                      )}
                    </td>
                    <td>
                      {build.visible_to_email ? (
                        <button
                          type="button"
                          className="link"
                          title={build.visible_to_email}
                          onClick={() => update.mutate({ id: build.id, visible_to_everyone: true })}
                        >
                          {t("readyBuilds.visibleOne", { email: build.visible_to_email })}
                        </button>
                      ) : (
                        <span className="badge passed">{t("readyBuilds.visibleAll")}</span>
                      )}
                    </td>
                    <td className="muted small nowrap">
                      {relativeTime(build.created_at, t)}
                      <br />
                      {build.created_by_email}
                    </td>
                    <td className="nowrap">
                      <div className="row-actions">
                        <button
                          type="button"
                          title={t("readyBuilds.linkHint")}
                          onClick={() => copyLink(build)}
                        >
                          {copied === build.id
                            ? t("readyBuilds.copied")
                            : t("readyBuilds.copyLink")}
                        </button>
                        <button
                          type="button"
                          disabled={download.isPending && download.variables === build.id}
                          onClick={() => download.mutate(build.id)}
                        >
                          {t("readyBuilds.download")}
                        </button>
                        {confirming === build.id ? (
                          <>
                            <button
                              type="button"
                              className="danger"
                              onClick={() => remove.mutate(build.id)}
                            >
                              {t("readyBuilds.confirmDelete")}
                            </button>
                            <button type="button" onClick={() => setConfirming(null)}>
                              {t("readyBuilds.cancel")}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="danger"
                            onClick={() => setConfirming(build.id)}
                          >
                            {t("readyBuilds.delete")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
